import React, { useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell, LabelList,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Legend,
} from 'recharts';
import { GAP_TYPES } from '../../data/gapKeywords';

function renderWithRedBrackets(text) {
  if (!text) return null;
  const parts = text.split(/(\[[^\]]+\])/g);
  return parts.map((part, i) => {
    if (/^\[.+\]$/.test(part)) {
      return (
        <span key={i} style={{ color: '#dc2626', fontWeight: 700 }}>
          {part}
        </span>
      );
    }
    return part;
  });
}

export default function GapOutput({
  context,
  gapStatement,
  gapFormula,
  validityScore,
  warnings,
  matrixData,
  adjustedGaps,
  onReset,
}) {
  const [copiedStatement, setCopiedStatement] = useState(false);
  const [copiedFormula, setCopiedFormula] = useState(false);

  const handleCopy = (text, setCopied) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  const handlePrint = () => window.print();

  const topThree = (adjustedGaps || []).slice(0, 3);

  const verdictColor =
    (validityScore?.percentage ?? 0) >= 80 ? '#22c55e' :
    (validityScore?.percentage ?? 0) >= 60 ? '#f59e0b' :
    (validityScore?.percentage ?? 0) >= 40 ? '#ef4444' : '#6b7280';

  const warningIcons = { danger: '🔴', warning: '🟡', info: '🔵' };

  return (
    <div className="gf-step gf-output">
      {/* ── Header ── */}
      <div className="gf-output-header">
        <div className="gf-step-badge">Phase 7</div>
        <h2>Gap Analysis Report</h2>
        <p className="gf-output-meta">
          Topic: <strong>{context.topic}</strong> &nbsp;|&nbsp;
          Discipline: <strong>{context.discipline}</strong> &nbsp;|&nbsp;
          Level: <strong>{context.academicLevel}</strong>
        </p>
        <div className="gf-output-actions">
          <button className="gf-btn gf-btn-outline" onClick={handlePrint}>🖨 Print / Save PDF</button>
          <button className="gf-btn gf-btn-secondary" onClick={onReset}>↺ Start New Analysis</button>
        </div>
      </div>

      {/* ── Gap Warnings ── */}
      {warnings && warnings.length > 0 && (
        <div className="gf-warnings-section">
          <h3>⚠ Gap Warnings</h3>
          {warnings.map((w, i) => (
            <div key={i} className={`gf-warning gf-warning-${w.severity}`}>
              {warningIcons[w.severity]} {w.message}
            </div>
          ))}
        </div>
      )}

      {/* ── Top 3 Gaps ── */}
      <div className="gf-output-section">
        <h3>Top Identified Gaps</h3>
        <div className="gf-gap-cards">
          {topThree.map((gap, idx) => (
            <div key={gap.type} className="gf-gap-card" style={{ borderLeftColor: GAP_TYPES[gap.type]?.color }}>
              <div className="gf-gap-card-rank" style={{ background: GAP_TYPES[gap.type]?.color }}>
                #{idx + 1}
              </div>
              <div className="gf-gap-card-body">
                <h3>{GAP_TYPES[gap.type]?.label}</h3>
                <p>{GAP_TYPES[gap.type]?.description}</p>
                <div className="gf-gap-meta">
                  <span>Adjusted Score: <strong>{gap.adjustedScore ?? gap.totalScore}</strong></span>
                  <span>Articles: <strong>{gap.articleCount}</strong></span>
                  <span>Coverage: <strong>{gap.percentage}%</strong></span>
                  <span>Confirmed: <strong style={{ textTransform: 'capitalize' }}>{gap.userConfirmation || '—'}</strong></span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Horizontal Bar Chart ── */}
      <div className="gf-output-section gf-chart-section">
        <h3>Gap Signal Rankings</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart
            data={matrixData?.barData || []}
            layout="vertical"
            margin={{ top: 5, right: 60, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" horizontal={false} />
            <XAxis type="number" allowDecimals={false} />
            <YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 12 }} />
            <Tooltip />
            <Bar dataKey="score" radius={[0, 6, 6, 0]} name="Signal Score">
              {(matrixData?.barData || []).map((entry, index) => (
                <Cell key={index} fill={entry.color} />
              ))}
              <LabelList dataKey="score" position="right" style={{ fontWeight: 700 }} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* ── Radar Chart ── */}
      <div className="gf-output-section gf-chart-section">
        <h3>Gap Radar Profile</h3>
        <ResponsiveContainer width="100%" height={340}>
          <RadarChart data={matrixData?.radarData || []} margin={{ top: 20, right: 40, bottom: 20, left: 40 }}>
            <PolarGrid />
            <PolarAngleAxis dataKey="subject" tick={{ fontSize: 11 }} />
            <PolarRadiusAxis angle={30} />
            <Radar name="Gap Strength" dataKey="score" stroke="#6366f1" fill="#6366f1" fillOpacity={0.35} />
            <Radar name="Article Count" dataKey="articles" stroke="#22c55e" fill="#22c55e" fillOpacity={0.2} />
            <Legend />
          </RadarChart>
        </ResponsiveContainer>
      </div>

      {/* ── Validity Score ── */}
      <div className="gf-output-section">
        <h3>Validity Score</h3>
        <div className="gf-validity-summary" style={{ borderColor: verdictColor }}>
          <div className="gf-validity-big-score" style={{ color: verdictColor }}>
            {validityScore?.total || 0}<span>/20</span>
          </div>
          <div>
            <h4 style={{ color: verdictColor }}>{validityScore?.verdict}</h4>
            <p>{validityScore?.percentage}% validity confidence</p>
          </div>
        </div>
      </div>

      {/* ── Gap Formula ── */}
      <div className="gf-output-section">
        <h3>Gap Formula Statement</h3>
        <div className="gf-formula-output">
          <p style={{ fontFamily: 'Georgia, serif', lineHeight: 1.9, fontSize: '15px' }}>
            {renderWithRedBrackets(gapFormula)}
          </p>
          <p style={{ fontSize: '13px', color: '#6b7280', marginTop: '6px' }}>
            Items in <span style={{ color: '#dc2626', fontWeight: 700 }}>red brackets</span> should be replaced with your specific details before use.
          </p>
          <button
            className="gf-btn gf-btn-outline gf-btn-sm"
            onClick={() => handleCopy(gapFormula, setCopiedFormula)}
          >
            {copiedFormula ? '✅ Copied!' : '📋 Copy Formula'}
          </button>
        </div>
      </div>

      {/* ── Full Gap Statement ── */}
      <div className="gf-output-section">
        <h3>Full Gap Statement (300+ words with citations)</h3>
        <div className="gf-statement-box">
        <pre className="gf-statement-text">{gapStatement || '⚠ Gap statement not yet generated. Please complete all 7 phases.'}</pre>
          <div className="gf-statement-actions">
            <button
              className="gf-btn gf-btn-primary"
              onClick={() => handleCopy(gapStatement, setCopiedStatement)}
            >
              {copiedStatement ? '✅ Copied!' : '📋 Copy Full Statement'}
            </button>
          </div>
        </div>
        <p className="gf-disclaimer">
          ⚠ <strong>Disclaimer:</strong> This gap statement is auto-generated based on keyword detection.
          The user is responsible for verifying its accuracy, relevance and academic integrity.
          Research Methodology Wizard bears no responsibility for any errors or misuse of this tool
          (Saunders et al., 2019; Creswell &amp; Creswell, 2018).
        </p>
      </div>

      {/* ── Keywords ── */}
      <div className="gf-output-section">
        <h3>Research Keywords Used</h3>
        <div className="gf-kw-cloud">
          {(context.keywords || []).filter((k) => k.trim()).map((kw, i) => (
            <span key={i} className="gf-kw-tag gf-kw-tag-lg">{kw}</span>
          ))}
        </div>
      </div>
    </div>
  );
}
